import React, { useEffect, useRef, useState } from 'react'
import { useDamagesStore, DamageType } from '../store/useDamagesStore'
import { AlertTriangle, Plus, Trash2, Edit2, Loader, Check, X, GripVertical } from 'lucide-react'

export function DamageManager() {
  const {
    damages,
    damagesLoading,
    damagesError,
    fetchDamages,
    addDamage,
    updateDamage,
    removeDamage,
    reorderDamages,
  } = useDamagesStore()

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editLabel, setEditLabel] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  const dragItem = useRef<number | null>(null)
  const dragOverItem = useRef<number | null>(null)

  useEffect(() => {
    fetchDamages()
  }, [fetchDamages])

  /* ── Drag & Drop ──────────────────────────────────────────────────────── */
  const handleDragStart = (index: number) => { dragItem.current = index }
  const handleDragEnter = (index: number) => { dragOverItem.current = index }
  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return
    const reordered = [...damages]
    const [moved] = reordered.splice(dragItem.current, 1)
    reordered.splice(dragOverItem.current, 0, moved)
    dragItem.current = null
    dragOverItem.current = null
    reorderDamages(reordered)
  }

  /* ── Édition inline ───────────────────────────────────────────────────── */
  const startEdit = (dmg: DamageType) => {
    setEditingId(dmg.id)
    setEditLabel(dmg.label)
  }
  const saveEdit = () => {
    if (!editingId) return
    if (!editLabel.trim()) return setEditingId(null)
    updateDamage(editingId, editLabel.trim())
    setEditingId(null)
  }

  /* ── Ajout ────────────────────────────────────────────────────────────── */
  const handleAdd = () => {
    if (!newLabel.trim()) return
    addDamage(newLabel.trim())
    setNewLabel('')
  }

  return (
    <div className="max-w-xl mx-auto font-sans p-4">
      <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
        <AlertTriangle size={20} className="text-orange-500" />
        Détails de dommages
      </h2>

      {/* ── Erreur / Loading ── */}
      {damagesLoading && (
        <div className="text-sm text-indigo-500 mb-4 flex items-center gap-2">
          <Loader size={16} className="animate-spin" /> Chargement des types...
        </div>
      )}
      {damagesError && !damagesLoading && (
        <div className="text-sm text-red-500 mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
          ⚠️ {damagesError} — Utilisation des valeurs hors-ligne ou par défaut.
        </div>
      )}

      {/* ── Liste des dommages (Draggable) ── */}
      <div className="flex flex-col gap-2">
        {damages.map((dmg, index) => (
          <div
            key={dmg.id}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragEnter={() => handleDragEnter(index)}
            onDragEnd={handleDragEnd}
            onDragOver={e => e.preventDefault()}
            className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-3 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing"
          >
            {/* Grip handle */}
            <div className="text-gray-300">
              <GripVertical size={18} />
            </div>

            {/* Label / Input edit */}
            <div className="flex-1 flex items-center min-w-0">
              {editingId === dmg.id ? (
                <input
                  value={editLabel}
                  onChange={e => setEditLabel(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && saveEdit()}
                  autoFocus
                  className="w-full bg-gray-50 border border-indigo-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-100 text-sm"
                />
              ) : (
                <span className="text-sm font-semibold text-gray-700 truncate">{dmg.label}</span>
              )}
            </div>

            {/* Hint d'ordre visuel */}
            <span className="text-xs text-gray-400 font-mono flex-shrink-0 w-6 text-right">
              #{index + 1}
            </span>

            {/* Actions */}
            <div className="flex gap-1">
              {editingId === dmg.id ? (
                <>
                  <button onClick={saveEdit} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                    <Check size={16} />
                  </button>
                  <button onClick={() => setEditingId(null)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">
                    <X size={16} />
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => startEdit(dmg)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors">
                    <Edit2 size={16} />
                  </button>
                  
                  {confirmDelete === dmg.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { removeDamage(dmg.id); setConfirmDelete(null) }}
                        className="p-1 px-2 text-xs bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition"
                      >
                        Sûr?
                      </button>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        className="p-1 px-2 text-xs bg-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-300 transition"
                      >
                        ✗
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmDelete(dmg.id)} className="p-1.5 text-red-400 hover:bg-red-50 hover:text-red-500 rounded-lg transition-colors">
                      <Trash2 size={16} />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Formulaire d'ajout ── */}
      <div className="mt-6 bg-indigo-50 border border-indigo-100 rounded-xl p-4">
        <h3 className="text-sm font-bold text-indigo-800 mb-3 flex items-center gap-1.5">
          <Plus size={16} /> Ajouter un dommage
        </h3>
        <div className="flex gap-2">
          <input
            placeholder="Nouveau type de dommage (ex: Bosse)"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            className="flex-1 bg-white border border-indigo-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent transition-shadow"
          />
          <button
            onClick={handleAdd}
            disabled={!newLabel.trim()}
            className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-bold px-4 py-2 rounded-xl transition-colors shadow-sm text-sm"
          >
            Ajouter
          </button>
        </div>
      </div>
    </div>
  )
}
